import type { DiaryForTodoCounters } from "@/features/diaries/hooks/diaryForTodoCounters";
import type { FormsToDoItem } from "@/features/diaries/features/new/hooks/useFormsToDo";
import { useJourneyCopy } from "@/features/i18n/journeyCopy";
import type { SupportedLocale } from "@patient-watch/i18n";
import type { StatefulDiaryT } from "@/types/database/diaries";
import { useEffect, useMemo } from "react";

import {
    buildJourneyPhases,
    buildFormsApplicableTodos,
    computePatientNotifications,
    countOutstandingJourneyTodos,
    countOutstandingJourneyTodosByPhase,
    isSetupApplicableForDiary,
    type PhaseProgress,
} from "../journeyTodos";
import { patientHasSkippedReferenceDateSetup } from "@/features/diaries/utils/missingDetailsBuilders";
import { usePatientJourneyStore, type PatientJourneySnapshot } from "../stores/patientJourneyStore";
import { getNextJourneyPhase } from "../utils/getNextJourneyPhase";
import { useAuth } from "@/utils/auth";
import { useJourneyTodos } from "./useJourneyTodos";

interface UsePatientJourneyOptions {
    diaryId?: string;
    onboardingProgress?: PhaseProgress;
    consentProgress?: PhaseProgress;
    setupProgress?: PhaseProgress;
    formsProgress?: PhaseProgress;
    includeFuturePhases?: boolean;
    formsToDo?: FormsToDoItem[];
    locale?: SupportedLocale;
    /** All patient diaries — enables nav badge counts via computePatientNotifications. */
    allDiaries?: readonly DiaryForTodoCounters[];
}

function buildPatientJourneySnapshot(
    diary: StatefulDiaryT | null | undefined,
    todos: ReturnType<typeof useJourneyTodos>,
    options: UsePatientJourneyOptions = {},
    formsApplicable: ReturnType<typeof buildFormsApplicableTodos> = [],
    authRoles?: { isClinicianOnly?: boolean; isPatient?: boolean },
    phaseLabels?: {
        onboarding: string;
        setup: string;
        consent: string;
        forms: string;
        diary: string;
    }
): PatientJourneySnapshot {
    const hasDiary = Boolean(diary);
    const setupApplicable = isSetupApplicableForDiary(diary);
    const setupSkippedIncomplete = diary ? patientHasSkippedReferenceDateSetup(diary) : false;
    const phases = buildJourneyPhases(todos, {
        onboardingProgress: options.onboardingProgress,
        consentProgress: options.consentProgress,
        setupProgress: options.setupProgress,
        formsProgress: options.formsProgress,
        includeFuturePhases: options.includeFuturePhases,
        hasDiary,
        setupApplicable,
        setupSkippedIncomplete,
        formsApplicable,
        phaseLabels: phaseLabels
            ? {
                  onboarding: phaseLabels.onboarding,
                  setup: phaseLabels.setup,
                  consent: phaseLabels.consent,
                  forms: phaseLabels.forms,
              }
            : undefined,
    });
    const allPhasesComplete = phases.length > 0 && phases.every((phase) => phase.completedSteps >= phase.totalSteps);
    const diaryId = options.diaryId ?? diary?.diary_id ?? null;
    const notifications =
        options.allDiaries && authRoles
            ? computePatientNotifications(todos, options.allDiaries, { roles: authRoles })
            : null;
    const outstandingCount = notifications
        ? countOutstandingJourneyTodos(todos, { formEntryCount: notifications.formEntryCount })
        : countOutstandingJourneyTodos(todos);

    return {
        diaryId,
        todos,
        phases,
        setupApplicable,
        allPhasesComplete,
        outstandingCount,
        todoCounts: countOutstandingJourneyTodosByPhase(todos),
        notifications,
        nextStage: diary
            ? getNextJourneyPhase({
                  diary,
                  diaryId: diary.diary_id,
                  todos,
                  setupApplicable,
                  formsToDo: options.formsToDo,
                  locale: options.locale,
                  phaseLabels: phaseLabels
                      ? {
                            consent: phaseLabels.consent,
                            setup: phaseLabels.setup,
                            forms: phaseLabels.forms,
                            diary: phaseLabels.diary,
                        }
                      : undefined,
              })
            : null,
    };
}

/** Single patient journey snapshot for the progress bar, nav badge, and success CTAs. */
export function usePatientJourney(diary?: StatefulDiaryT | null, options: UsePatientJourneyOptions = {}) {
    const auth = useAuth();
    const journeyCopy = useJourneyCopy();
    const todos = useJourneyTodos(diary);
    const formsApplicable = diary ? buildFormsApplicableTodos(diary, { roles: auth.roles }) : [];
    const phaseLabels = {
        onboarding: journeyCopy.phaseProfile,
        setup: journeyCopy.phaseSetup,
        consent: journeyCopy.phaseConsent,
        forms: journeyCopy.phaseForms,
        diary: journeyCopy.phaseDiary,
    };
    const {
        diaryId,
        onboardingProgress,
        consentProgress,
        setupProgress,
        formsProgress,
        includeFuturePhases,
        formsToDo,
        locale,
        allDiaries,
    } = options;
    const snapshot = useMemo(
        () =>
            buildPatientJourneySnapshot(
                diary,
                todos,
                {
                    diaryId,
                    onboardingProgress,
                    consentProgress,
                    setupProgress,
                    formsProgress,
                    includeFuturePhases,
                    formsToDo,
                    locale,
                    allDiaries,
                },
                formsApplicable,
                auth.roles,
                phaseLabels
            ),
        [
            allDiaries,
            auth.roles,
            consentProgress,
            diary,
            diaryId,
            formsApplicable,
            formsProgress,
            formsToDo,
            includeFuturePhases,
            locale,
            onboardingProgress,
            phaseLabels.onboarding,
            phaseLabels.setup,
            phaseLabels.consent,
            phaseLabels.forms,
            phaseLabels.diary,
            setupProgress,
            todos,
        ]
    );

    useEffect(() => {
        usePatientJourneyStore.getState().setSnapshot(snapshot);
    }, [snapshot]);

    return snapshot;
}

export function selectPatientJourneyOutstandingCount(snapshot: PatientJourneySnapshot | null): number {
    return snapshot?.outstandingCount ?? 0;
}
